import _ from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { IffEditor } from '@bingads-webui-campaign/ad-customizers/src/iff/editor/iff-editor';
import { AudienceSelectorLegacyViewManager } from '@bingads-webui-campaign/ad-customizers/src/iff/editor/audience-selector';
import { parseRule } from '@bingads-webui-campaign/ad-customizers/src/iff/parser';
import { IF_FUNCTION_TYPE } from '@bingads-webui-campaign/ad-customizers/src/iff/if-function-type';
import { escapeIffAudienceName, escapeIffInsertText, escapeIffDefaultText } from '@bingads-webui-campaign/ad-customizers/src/iff/escape';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';

const isSelectArrow = element =>
  _.isFunction(element.className.indexOf) && element.className.indexOf('arrow-button') !== -1 &&
  element.parentElement !== null &&
  _.isFunction(element.parentElement.className.indexOf) && element.parentElement.className.indexOf('dlbItem-actions') !== -1;

const isRemoveArrow = element =>
  _.isFunction(element.className.indexOf) && element.className.indexOf('arrow-button') !== -1 &&
  element.className.indexOf('remove') !== -1 &&
  element.parentElement !== null &&
  _.isFunction(element.parentElement.className.indexOf) && element.parentElement.className.indexOf('dlbItem') !== -1;


export class AdCustomizerIffAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({
    deps: {
      i18n, instrumentation, audienceSelectorLegacyViewManager, perfMarker, permissions,
    }, controller,
  }) {
    super({
      name: 'iff',
      priority: 6000,
      i18n,
      activityName: 'iffEditor',
      instrumentation,
      controller,
    });
    this.controller = controller;
    this.audienceSelectorLegacyViewManager =
      audienceSelectorLegacyViewManager || new AudienceSelectorLegacyViewManager({ perfMarker });

    this.onEditorSave = this.onEditorSave.bind(this);
    this.onEditorCancel = this.onEditorCancel.bind(this);

    const { IsNewAudienceEnabled, IsIFFAudienceSyntaxUpdateEnabled } = permissions;
    this.IsNewAudienceEnabled = IsNewAudienceEnabled;
    this.IsIFFAudienceSyntaxUpdateEnabled = IsIFFAudienceSyntaxUpdateEnabled;
  }

  getSyntax(/* analyzeResult */) {
    // {=IF(Condition, insert text):default text}
    const syntax = this.i18n.getString(_TL_('Syntax:'));
    const condition = this.i18n.getString(_TL_('Condition'));
    const insertText = this.i18n.getString(_TL_('insert text'));
    const defaultText = this.i18n.getString(_TL_('default text'));
    const ifExpression = ' {=IF(';
    this.syntax = `${syntax}${ifExpression}${condition}, ${insertText}):${defaultText}}`;
    return (
      <div>
        <span
          className="iff-syntax"
        >{this.syntax}
        </span>
        <HelpPopupInline
          topic="pop_BA_NewAdCreate_IFfunction_Syntax"
          ariaLabel={this.i18n.getString(_TL_('Click to learn more about {{item}}', { item: this.i18n.getString(_TL_('Syntax')) }))}
        />
      </div>
    );
  }

  needEditor(analyzeResult) {
    return !!analyzeResult;
  }

  onEditorSave({
    target, analyzeResult, updateAndCloseCallback,
  }, eventData) {
    const {
      source,
      startIndex,
    } = analyzeResult;
    const {
      insertText,
      defaultText,
      type,
      audienceList,
    } = eventData;

    const { newValue, cursorPosition } = this.formatExpression({
      audienceList,
      buildCompleteExpression: true,
      defaultText,
      insertText,
      source,
      startIndex,
      type,
      value: target.value,
    });


    updateAndCloseCallback({
      newValue,
      cursorPosition,
      isSelectedByUser: true,
    });
  }

  onEditorCancel({ updateAndCloseCallback }/* , eventData */) {
    updateAndCloseCallback({ isCancel: true });
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback,
  }) {
    const {
      source,
      insertText = 'insert text',
      defaultText = 'default text',
      type,
      audienceList = [],
    } = analyzeResult;

    const editorProps = {
      i18n: this.i18n,
      source,
      type,
      audienceList,
      insertText,
      defaultText,
      audienceTargetsView: this.audienceSelectorLegacyViewManager.audienceTargetsView,
      IsNewAudienceEnabled: this.IsNewAudienceEnabled,
      onSave: _.partial(this.onEditorSave, {
        target, analyzeResult, updateAndCloseCallback,
      }),
      onCancel: _.partial(this.onEditorCancel, {
        updateAndCloseCallback,
      }),
    };

    return (
      <IffEditor key={_.uniqueId()} {...editorProps} />
    );
  }

  formatExpression({
    audienceList,
    buildCompleteExpression = false,
    defaultText,
    insertText,
    source,
    startIndex = 0,
    type,
    value,
  }) {
    let actualType = '';
    let conditionText = '';
    if (type === IF_FUNCTION_TYPE.AUDIENCE_IN) {
      actualType = this.IsIFFAudienceSyntaxUpdateEnabled ? 'audience IN ' : 'audience in';
      conditionText = `(${audienceList.map(escapeIffAudienceName).join(',')})`;
    } else if (buildCompleteExpression || type === IF_FUNCTION_TYPE.DEVICE_MOBILE) {
      actualType = 'device=mobile';
    }
    const endIndex = startIndex + source.length;
    const beginning = value.substring(0, startIndex);
    const end = value.substring(endIndex);
    const expressionStart = '{=IF(';
    const insertTextPart = escapeIffInsertText(insertText);
    const defaultTextPart = _.isEmpty(defaultText) ? '' : (`:${escapeIffDefaultText(defaultText)}`);
    const newValue = `${beginning}${expressionStart}${actualType}${conditionText},${insertTextPart})${defaultTextPart}}${end}`;
    const cursorPosition = buildCompleteExpression ?
      newValue.length - end.length :
      beginning.length + expressionStart.length;

    return {
      newValue,
      cursorPosition,
    };
  }

  // get menu item
  getMenuItem({
    startIndex, rule, updateAndCloseCallback, target,
  }) {
    return {
      name: this.i18n.getString(_TL_('IF function')),
      description: this.i18n.getString(_TL_('Insert text when a condition is met')),
      onClick: () => {
        const beginning = target.value.substring(0, startIndex);
        const end = target.value.substring(startIndex + rule.source.length);
        const expressionStart = '{=IF(';
        const newValue = `${beginning}{=IF(,insert text):default text}${end}`;
        const cursorPosition = beginning.length + expressionStart.length;
        updateAndCloseCallback({
          newValue,
          cursorPosition,
          isSelectedByUser: true,
        });

        const analyzeResult = parseRule({
          text: newValue,
          cursorIndex: cursorPosition,
          startIndex,
        });


        this.controller.updatePicker({
          target,
          syntax: this.getSyntax(),
          editorView: this.getEditor({
            target,
            analyzeResult,
            updateAndCloseCallback,
          }),
          container: this.controller.container,
        });
      },
    };
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target,
  }) {
    const startIndex = text.lastIndexOf('{', cursorIndex);

    if (startIndex === -1 || startIndex === cursorIndex) {
      return Promise.resolve(null);
    }

    const rule = parseRule({
      text,
      cursorIndex,
      startIndex,
    });

    // The parser should have found an exression on the left from
    // the cursor AND cursor should be within the matched part of the string
    if (rule && cursorIndex <= startIndex + rule.source.length &&
       // AND cursor shouldn't be at the expression's closing "}"
       !(
         rule.isComplete &&
         cursorIndex === startIndex + rule.source.length
       ) &&
       // AND either we have found first significant character (isStarted)
       // or we are not past two characters after startIndex, inclusive
       (rule.isStarted || cursorIndex <= startIndex + 2)
    ) {
      return Promise.resolve(_.defaults({
        itemsToDisplay: [this.getMenuItem({
          startIndex,
          rule,
          target,
          updateAndCloseCallback,
        })],
        match: rule.isComplete,
        startIndex,
      }, rule));
    }

    return Promise.resolve(null);
  }

  /**
   * Returns `true` if click was not on an audience selection or removal buttons.
   * The default logic in the controller, which traces the event's target up
   * the UI tree to see if it was under one of the controller's managed elements
   * doesn't work with audiences, because by the time the click handler of controller
   * is called, the audience selector has already removed the arrow button from DOM.
   * @param {Object} event the click event.
   * @returns {Boolean} Returns `true` if click was not on an audience selection or removal buttons.
   * Otherwise, `false`.
   */
  isClickedAway(event) {
    return !isSelectArrow(event.target) &&
      !isRemoveArrow(event.target);
  }

  getCharCountExcludingAdCustomizers(text, isComplete) {
    let processedText = text;
    let charCount = 0;
    const rule = parseRule({
      text: processedText,
      startIndex: 0,
    });

    if (rule && rule.source && (!isComplete || rule.isComplete)) {
      const {
        insertText = '',
        defaultText = '',
      } = rule;
      if (rule.isComplete) {
        charCount += Math.max(insertText.length, defaultText.length);
      } else {
        charCount += rule.source.length;
      }
      processedText = processedText.replace(rule.source, '');
    }

    return {
      processedText,
      charCount,
    };
  }

  evaluateRule(text) {
    let processedText = text;
    let rule = parseRule({
      text: processedText,
      startIndex: 0,
    });

    while (rule && rule.source) {
      if (rule.isComplete) {
        const {
          insertText = '',
        } = rule;
        processedText = processedText.replace(rule.source, insertText);
      }

      rule = parseRule({
        text: processedText,
        startIndex: rule.index + rule.source.length,
      });
    }

    return Promise.resolve(processedText);
  }
}
